import * as Sentry from '@sentry/react'
import { matchPath } from 'react-router-dom'
import { browserHistory } from './browserHistory'
import { ENV, FUNCTIONS_API_BASE_URL, SENTRY_DSN, SENTRY_RELEASE } from 'constants/index'

// Route patterns for naming navigation transactions. Longest path under the
// same parent first, so that matchPath picks the most specific one.
const routes = [
  { path: '/product/edit/:productId' },
  { path: '/product' },
  { path: '/products' },
  { path: '/reception/:id/product/add' },
  { path: '/reception/edit/:id' },
  { path: '/reception' },
  { path: '/auctions/:id/seller-commissions/:sellerCommissionId/products' },
  { path: '/auctions/:id/seller-commissions/:sellerCommissionId' },
  { path: '/auctions/:id/seller-commissions' },
  { path: '/auctions/:id/palette-numbers' },
  { path: '/auctions/:id/open-orders' },
  { path: '/auctions/:id/orders' },
  { path: '/auctions/:id/live' },
  { path: '/auctions/edit/:id' },
  { path: '/auctions' },
  { path: '/clients/:id/view' },
  { path: '/clients/:id' },
  { path: '/clients' },
  { path: '/users/edit/:id' },
  { path: '/users' },
  { path: '/fast-track-receptions/:id' },
  { path: '/fast-track-receptions' },
  { path: '/category/edit/:id' },
  { path: '/category' },
  { path: '/tag/edit/:id' },
  { path: '/tag' },
  { path: '/history-items' },
  { path: '/payments' },
  { path: '/profile' },
  { path: '/home' },
  { path: '/' },
]

// Firestore's WebChannel transport sends request headers, including the
// signed-in user's Firebase ID token, in the query string of its first POST.
// The admin panel keeps table filters in its own query string (`?table=...`),
// which can hold what a user typed into a filter, such as a customer's name.
// Those URLs end up in events, breadcrumbs and spans, so strip the query from
// both before anything leaves the browser.
const FIRESTORE_HOST = 'firestore.googleapis.com'
const isStripped = url => url.startsWith('/') || url.includes(window.location.origin) || url.includes(FIRESTORE_HOST)
const stripQuery = url => (typeof url === 'string' && isStripped(url) ? url.split('?')[0] : url)

const scrubUrlFields = obj => {
  if (!obj) return
  for (const key of ['url', 'http.url', 'from', 'to']) {
    if (obj[key]) obj[key] = stripQuery(obj[key])
  }
}

const scrubRequest = event => {
  if (!event.request) return
  event.request.url = stripQuery(event.request.url)
  if (event.request.query_string) delete event.request.query_string
  if (event.request.headers?.Referer) event.request.headers.Referer = stripQuery(event.request.headers.Referer)
}

export const initSentry = () => {
  if (!SENTRY_DSN) return

  Sentry.init({
    dsn: SENTRY_DSN,
    release: SENTRY_RELEASE,
    environment: ENV,
    beforeBreadcrumb(breadcrumb) {
      scrubUrlFields(breadcrumb.data)
      return breadcrumb
    },
    beforeSend(event) {
      scrubRequest(event)
      return event
    },
    beforeSendTransaction(event) {
      scrubRequest(event)
      event.spans?.forEach(span => {
        span.description = stripQuery(span.description)
        scrubUrlFields(span.data)
      })
      return event
    },
    integrations: [
      Sentry.reactRouterV5BrowserTracingIntegration({ history: browserHistory, routes, matchPath }),
      Sentry.replayIntegration(),
    ],
    // The admin panel has a handful of users, so tracing every page load and
    // navigation stays well inside the plan quota.
    tracesSampleRate: 1.0,
    // Attach trace headers to adminApi calls so server time shows up in the same trace.
    tracePropagationTargets: [FUNCTIONS_API_BASE_URL],
    // The plan includes 50 replays a month. Recording every session spent them
    // within hours of the cycle starting and left none for the sessions that
    // error, which are the ones worth watching. Timings come from tracing above,
    // not from replays, so the slowness work is unaffected.
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 1.0,
  })
}
